import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStore } from '../lib/store'
import type { AppAlert } from '../lib/types'
import { alertText } from '../lib/alert'
import { GhostMark } from './GhostMark'
import './alerts.css'

const TOAST_MS = 6500
const MAX_VISIBLE = 4

// Transient toasts for newly-arriving live alerts. Mounted once inside the
// authed Shell; it watches the store's alert list and pops a toast for each
// genuinely-new alert, auto-dismissing after a few seconds. Pre-existing alerts
// at mount are "primed" (marked seen) so navigating around never re-toasts them.
export function AlertToasts() {
  const { alerts } = useStore()
  const nav = useNavigate()
  const [toasts, setToasts] = useState<AppAlert[]>([])
  const seen = useRef<Set<string>>(new Set())
  const primed = useRef(false)
  const timers = useRef<number[]>([])

  useEffect(() => {
    if (!primed.current) {
      primed.current = true
      alerts.forEach((a) => seen.current.add(a.id))
      return
    }
    const fresh = alerts.filter((a) => !seen.current.has(a.id))
    if (!fresh.length) return
    fresh.forEach((a) => seen.current.add(a.id))
    setToasts((prev) => [...fresh, ...prev].slice(0, MAX_VISIBLE))
    fresh.forEach((a) => {
      const t = window.setTimeout(
        () => setToasts((prev) => prev.filter((x) => x.id !== a.id)),
        TOAST_MS,
      )
      timers.current.push(t)
    })
  }, [alerts])

  // Clear any pending timers when the Shell unmounts (e.g. on sign-out).
  useEffect(() => () => timers.current.forEach(clearTimeout), [])

  if (!toasts.length) return null

  function open(a: AppAlert) {
    setToasts((prev) => prev.filter((x) => x.id !== a.id))
    // Announcements aren't candidate-bound; the toast just dismisses.
    if (a.kind === 'announcement' || !a.candidateId) return
    nav(`/candidates/${a.candidateId}`)
  }

  return (
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((t) => (
        <button key={t.id} type="button" className="toast" onClick={() => open(t)}>
          <GhostMark size={26} tint={t.kind === 'announcement' ? 'vault' : 'live'} breathing />
          <span className="toast__body">
            <b>{t.kind === 'announcement' ? t.title : t.candidateName}</b>
            <small>{t.kind === 'announcement' ? 'Announcement' : alertText(t.kind)}</small>
          </span>
        </button>
      ))}
    </div>
  )
}
